import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/authAPI';
import { 
  validateEmail, 
  validatePassword, 
  validatePasswordConfirmation, 
  validatePhone, 
  validateSecurityAnswer, 
  formatAPIError 
} from '../utils/validation';
import PasswordInput from '../components/common/PasswordInput';
import './styles/Register.css';

const Register = () => {
  const navigate = useNavigate();
    const location = useLocation();

  const { isAuthenticated } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    countryCode: '+1',
    phone: '',
    fullPhone: '',
    password: '',
    confirmPassword: '',
    role: 'CANDIDATE', 
    securityQuestion1: 'What is your mother\'s name?',
    securityAnswer1: '',
    securityQuestion2: 'What is your best friend name?',
    securityAnswer2: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [emailSending, setEmailSending] = useState(false);
const isRegisterRoute = location.pathname === "/register";


  // Country codes with flags
  const countryCodes = [
    { code: '+1', name: 'US/CA', flag: '🇺🇸' },
    { code: '+44', name: 'UK', flag: '🇬🇧' },
    { code: '+91', name: 'India', flag: '🇮🇳' },
    { code: '+86', name: 'China', flag: '🇨🇳' },
    { code: '+33', name: 'France', flag: '🇫🇷' },
    { code: '+49', name: 'Germany', flag: '🇩🇪' },
    { code: '+81', name: 'Japan', flag: '🇯🇵' },
    { code: '+61', name: 'Australia', flag: '🇦🇺' }
  ];

  // Security questions options
  const securityQuestions = [
    'What is your mother\'s name?',
    'What is your best friend name?',
    'What was the name of your first pet?',
    'In which city were you born?',
    'What is your favorite color?',
    'What was your childhood nickname?',
    'What is the name of your favorite teacher?',
    'What was your first car?'
  ];

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const fullPhone = formData.phone ? formData.countryCode + formData.phone : formData.countryCode;
    if (fullPhone !== formData.fullPhone) {
      setFormData(prev => ({ ...prev, fullPhone }));
    }
  }, [formData.countryCode, formData.phone, formData.fullPhone]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name] || errors.submit) {
      setErrors(prev => ({ ...prev, [name]: '', submit: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    if (formData.phone) {
      const phoneError = validatePhone(formData.fullPhone);
      if (phoneError) newErrors.phone = phoneError;
    }

    const passwordError = validatePassword(formData.password);
    if (passwordError) newErrors.password = passwordError;

    const confirmPasswordError = validatePasswordConfirmation(formData.password, formData.confirmPassword);
    if (confirmPasswordError) newErrors.confirmPassword = confirmPasswordError;

    const answer1Error = validateSecurityAnswer(formData.securityAnswer1);
    if (answer1Error) newErrors.securityAnswer1 = answer1Error;

    const answer2Error = validateSecurityAnswer(formData.securityAnswer2);
    if (answer2Error) newErrors.securityAnswer2 = answer2Error;

    if (formData.securityQuestion1 === formData.securityQuestion2) {
      newErrors.securityQuestion2 = 'Please select different security questions';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors({});
    setSuccess('');
    setEmailSending(false);

    try {
      // Step 1: Register the user
      const registrationData = {
        email: formData.email.trim(),
        password: formData.password,
        role: formData.role,
        securityQuestions: {
          securityQuestion1: formData.securityQuestion1,
          securityAnswer1: formData.securityAnswer1.trim(),
          securityQuestion2: formData.securityQuestion2,
          securityAnswer2: formData.securityAnswer2.trim()
        }
      };

      // Add phone only if provided
      if (formData.fullPhone && formData.fullPhone !== formData.countryCode) {
        registrationData.phone = formData.fullPhone;
      }

      console.log('🚀 Registration payload:', registrationData);
      const response = await authAPI.register(registrationData);
      console.log('✅ Registration successful:', response.data);

      // Step 2: Send verification email after successful registration
      let emailSent = false;
      if (registrationData.email) {
        try {
          setEmailSending(true);
          console.log('📧 Sending verification email to:', registrationData.email);
          await authAPI.resendVerification(registrationData.email);
          console.log('✅ Verification email sent successfully!');
          emailSent = true;
          setSuccess('🎉 Account created successfully! Verification email sent to your inbox.');
        } catch (emailError) {
          console.error('❌ Failed to send verification email:', emailError);
          setSuccess('🎉 Account created successfully! Please request verification email from the verification page.');
        } finally {
          setEmailSending(false);
        }
      } else {
        setSuccess('🎉 Account created successfully!');
      }

      // Step 3: Navigate to verification page
      setTimeout(() => {
        if (registrationData.phone && !registrationData.email) {
          // Phone-only registration
          navigate('/verification', {
            state: {
              phone: registrationData.phone,
              message: 'A 6-digit verification code has been sent to your phone.'
            }
          });
        } else if (registrationData.email) {
          // Email registration (with or without phone)
          navigate('/verification', {
            state: {
              email: registrationData.email,
              phone: registrationData.phone || null,
              message: emailSent 
                ? 'A verification link has been sent to your email address. Please check your inbox and spam folder.'
                : 'Please verify your email address to continue. You can request a new verification email if needed.'
            }
          });
        }
      }, 2000);

    } catch (error) {
      console.error('❌ Registration error:', error);
      const errorMessage = formatAPIError(error);
      setErrors({ submit: errorMessage });
      setEmailSending(false);
    } finally {
      setLoading(false);
    }
  };

  // Show success state
  // Show success state
if (success) {
    return (
      <>
      <div className="login-page">
        <div className="login-layout">
          <div className="login-left">
            <div className="login-left-content">
              <h1 className="login-left-title">Sign in to</h1>
              <p className="login-left-subtitle">Job Portal</p>
            </div>
          </div>
          
          <div className="login-right">
            <div className="success-container">
              <div className="success-icon-wrapper">
                <svg 
                  width="48" 
                  height="48" 
                  fill="none" 
                  stroke="#22c55e" 
                  strokeWidth="3" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>
              <h2 className="success-title">Registration Successful!</h2>
              <p className="success-subtitle">{success}</p>
              {emailSending && (
                <p className="email-sending-status">📧 Sending verification email...</p>
              )}
              <p className="email-sending-status">Redirecting you to verification page...</p>
            </div>
          </div>
        </div>
        </div>
      </>
    );
  }

  return (
    <>
          <div className="login-page">

      <div className="login-layout">
        {/* LEFT SIDE */}
        <div className="login-left">
          <div className="login-left-content">
            <h1 className="login-left-title">Sign up to</h1>
            <p className="login-left-subtitle">Job Portal</p>

            <div className="login-left-register">
              <p>If you have an account registered</p>
              <p>
                You can{" "}
                <Link to="/login" className="login-left-register-link">
                  Sign in here !
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE - FORM */}
        <div className="login-right">
          <div className="register-form-wrapper">
            {/* Top nav buttons */}
          <div className="auth-nav">
            <button
              type="button"
              className={`toggle-btn ${!isRegisterRoute ? "active" : ""}`}
              onClick={() => navigate("/login")}
            >
              Sign In
            </button>

            <button
              type="button"
              className={`toggle-btn ${isRegisterRoute ? "active" : ""}`}
              onClick={() => navigate("/register")}
            >
              Join Now
            </button>
          </div>
            <h1 className="login-title">Sign up</h1>

            <form onSubmit={handleSubmit} className="login-form" noValidate>
              {/* Submit Error */}
              {errors.submit && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  {errors.submit}
                </div>
              )}

              {/* Email Input */}
              <div className="form-group">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  placeholder="Enter email"
                  required
                  disabled={loading}
                />
                {errors.email && <div className="error-text">{errors.email}</div>}
              </div>

              {/* Phone Input */}
              <div className="form-group">
                <label htmlFor="phone" className="form-label">
      
                </label>
                <div className={`phone-input-group ${errors.phone ? 'error' : ''}`}>
                  <select
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleChange}
                    className="country-code-select"
                    disabled={loading}
                  >
                    {countryCodes.map(country => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.code}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="phone-number-input"
                    placeholder="Phone Number (Optional)"
                    disabled={loading}
                  />
                </div>
                {errors.phone && <div className="error-text">{errors.phone}</div>}
              </div>

              {/* Role Selection */}
              <div className="form-group">
                
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className={`form-select ${errors.role ? 'error' : ''}`}
                  required
                  disabled={loading}
                >
                  <option value="CANDIDATE">Job Seeker / Candidate</option>
                  <option value="EMPLOYER">Employer / Company</option>
                  <option value="AGENCY">Verification Agency</option>
                </select>
                {errors.role && <div className="error-text">{errors.role}</div>}
                
                <div className="role-helper-text">
                  {formData.role === 'CANDIDATE' && 'Create your verified professional profile and apply for jobs'}
                  {formData.role === 'EMPLOYER' && 'Post job listings and hire verified candidates'}
                  {formData.role === 'AGENCY' && 'Provide verification services to employers and candidates'}
                </div>
              </div>

              {/* Password */}
              <div className="form-group">
                
                <PasswordInput
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  error={errors.password}
                  placeholder="Create a strong password"
                  disabled={loading}
                  className="form-input"
                />
                {errors.password && <div className="error-text">{errors.password}</div>}
              </div>

              {/* Confirm Password */}
              <div className="form-group">
                <PasswordInput
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={errors.confirmPassword}
                  placeholder="Re-enter your password"
                  disabled={loading}
                  className="form-input"
                />
                {errors.confirmPassword && <div className="error-text">{errors.confirmPassword}</div>}
              </div>

              {/* Security Questions */}
              <div className="security-questions-section">
                <h3 className="security-questions-title">Security Questions</h3>
                
                {/* Question 1 */}
                <div className="form-group">
                  <label htmlFor="securityQuestion1" className="form-label">
                    Security Question 1 
                  </label>
                  <select
                    id="securityQuestion1"
                    name="securityQuestion1"
                    value={formData.securityQuestion1}
                    onChange={handleChange}
                    className="form-select"
                    required
                    disabled={loading}
                  >
                    {securityQuestions.map((question, index) => (
                      <option key={index} value={question}>{question}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  
                  <input
                    type="text"
                    id="securityAnswer1"
                    name="securityAnswer1"
                    value={formData.securityAnswer1}
                    onChange={handleChange}
                    className={`form-input ${errors.securityAnswer1 ? 'error' : ''}`}
                    placeholder="Enter your answer"
                    required
                    disabled={loading}
                  />
                  {errors.securityAnswer1 && <div className="error-text">{errors.securityAnswer1}</div>}
                </div>

                {/* Question 2 */}
                <div className="form-group">
                  <label htmlFor="securityQuestion2" className="form-label">
                    Security Question 2 
                  </label>
                  <select
                    id="securityQuestion2"
                    name="securityQuestion2"
                    value={formData.securityQuestion2}
                    onChange={handleChange}
                    className={`form-select ${errors.securityQuestion2 ? 'error' : ''}`}
                    required
                    disabled={loading}
                  >
                    {securityQuestions.map((question, index) => (
                      <option key={index} value={question}>{question}</option>
                    ))}
                  </select>
                  {errors.securityQuestion2 && <div className="error-text">{errors.securityQuestion2}</div>}
                </div>

                <div className="form-group">
                  
                  <input
                    type="text"
                    id="securityAnswer2"
                    name="securityAnswer2"
                    value={formData.securityAnswer2}
                    onChange={handleChange}
                    className={`form-input ${errors.securityAnswer2 ? 'error' : ''}`}
                    placeholder="Enter your answer"
                    required
                    disabled={loading}
                  />
                  {errors.securityAnswer2 && <div className="error-text">{errors.securityAnswer2}</div>}
                </div>
              </div>

              {/* Submit Button */}
              <button 
                type="submit" 
                className="simple-login-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="loading-spinner"></span>
                    Creating Account...
                  </>
                ) : (
                  'Create Account'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
      </div>
    </>
  );

};

export default Register;
